import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AppLayout } from "@/components/layout/AppLayout";
import { Loading } from "@/components/common";

import Login from "@/pages/Login";
import Privacy from "@/pages/Privacy";
import Account from "@/pages/Account";

// Client pages
import Dashboard from "@/pages/client/Dashboard";
import Plan from "@/pages/client/Plan";
import Ceremony from "@/pages/client/Ceremony";
import MenuPage from "@/pages/client/MenuPage";
import Drinks from "@/pages/client/Drinks";
import Guests from "@/pages/client/Guests";
import Suppliers from "@/pages/client/Suppliers";
import MoodBoard from "@/pages/client/MoodBoard";
import Consent from "@/pages/client/Consent";
import RunningOrder from "@/pages/client/RunningOrder";
import RunSheet from "@/pages/client/RunSheet";
import PreferredSuppliers from "@/pages/client/PreferredSuppliers";
import Resources from "@/pages/client/Resources";
import Invoices from "@/pages/client/Invoices";
import Venue from "@/pages/client/Venue";
import Forms from "@/pages/client/Forms";
import FloorPlans from "@/pages/FloorPlans";
import InHouseStyling from "@/pages/InHouseStyling";
import NoEvent from "@/pages/client/NoEvent";
import { useGet } from "@/hooks/useResource";

// Staff pages
import StaffDashboard from "@/pages/staff/StaffDashboard";
import EventsList from "@/pages/staff/EventsList";
import EventDetail from "@/pages/staff/EventDetail";
import ResourceLibrary from "@/pages/staff/ResourceLibrary";
import StaffSuppliers from "@/pages/staff/StaffSuppliers";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <Loading label="Loading your portal" />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function ClientRoutes() {
  const { data, loading } = useGet("/client/context");
  if (loading) return <Loading label="Loading your event" />;
  if (!data?.has_event) return <NoEvent />;
  return (
    <Routes>
      <Route element={<AppLayout />}>
        <Route path="/" element={<Dashboard />} />
        <Route path="/plan" element={<Plan />} />
        <Route path="/ceremony" element={<Ceremony />} />
        <Route path="/menu" element={<MenuPage />} />
        <Route path="/drinks" element={<Drinks />} />
        <Route path="/guests" element={<Guests />} />
        <Route path="/suppliers" element={<Suppliers />} />
        <Route path="/mood-board" element={<MoodBoard />} />
        <Route path="/consent" element={<Consent />} />
        <Route path="/running-order" element={<RunningOrder />} />
        <Route path="/run-sheet" element={<RunSheet />} />
        <Route path="/preferred-suppliers" element={<PreferredSuppliers />} />
        <Route path="/resources" element={<Resources />} />
        <Route path="/invoices" element={<Invoices />} />
        <Route path="/floor-plans" element={<FloorPlans />} />
        <Route path="/forms" element={<Forms />} />
        <Route path="/venue-styling" element={<InHouseStyling />} />
        <Route path="/venue" element={<Venue />} />
        <Route path="/account" element={<Account />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function StaffRoutes() {
  return (
    <Routes>
      <Route element={<AppLayout />}>
        <Route path="/" element={<StaffDashboard />} />
        <Route path="/events" element={<EventsList />} />
        <Route path="/events/:id" element={<EventDetail />} />
        <Route path="/resource-library" element={<ResourceLibrary category="other" />} />
        <Route path="/venue" element={<Venue />} />
        <Route path="/floor-plans" element={<FloorPlans />} />
        <Route path="/forms" element={<ResourceLibrary category="form" />} />
        <Route path="/venue-styling" element={<InHouseStyling />} />
        <Route path="/preferred-suppliers" element={<StaffSuppliers />} />
        <Route path="/account" element={<Account />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function RoleRouter() {
  const { user } = useAuth();
  const isStaff = user?.role && user.role !== "client";
  return isStaff ? <StaffRoutes /> : <ClientRoutes />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route
        path="/*"
        element={
          <Protected>
            <RoleRouter />
          </Protected>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <div className="App font-body">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
